// src/features/developers/components/DownloadsTab.tsx
//
// The files are behind the sign-in, so they are fetched with the person's session and handed to
// the browser as a blob: a plain link would arrive without it.
import React, { useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { Box, Button, Card, Typography, useTheme } from '@mui/material';
import { DownloadRounded as DownloadIcon } from '@mui/icons-material';
import { developersApi } from '../api';
import type { DeveloperInfo } from '../types';
import { environmentName, InlineText, MONO } from './common';

interface DownloadsTabProps {
  info: DeveloperInfo | null;
  specText: string;
}

export const saveAs = (fileName: string, content: string, type = 'application/json'): void => {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export const DownloadsTab: React.FC<DownloadsTabProps> = ({ info, specText }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const theme = useTheme();
  const [busy, setBusy] = useState<string | null>(null);
  const t = (key: string, options?: Record<string, unknown>): string => translate(`developers.downloads.${key}`, options);

  const download = (id: string, fileName: string, load: () => Promise<string>): void => {
    setBusy(id);
    load()
      .then((text) => saveAs(fileName, text))
      .catch(() => notify('developers.errors.download', { type: 'error' }))
      .finally(() => setBusy(null));
  };

  const cardSx = { p: { xs: 2, sm: 2.5 }, minWidth: 0 } as const;
  const h2 = { fontSize: '1.0625rem', fontWeight: 600, m: 0 } as const;
  const body = { fontSize: '0.875rem', color: theme.palette.text.secondary, lineHeight: 1.65, mt: 1 } as const;

  return (
    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fit, minmax(min(380px, 100%), 1fr))' }}>
      <Card sx={cardSx}>
        <Typography component="h2" sx={h2}>
          {t('postman.title')}
        </Typography>
        <Typography sx={body}>{t('postman.text')}</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
          <Button
            variant="contained"
            size="small"
            disabled={busy === 'collection'}
            startIcon={<DownloadIcon />}
            onClick={() => download('collection', 'insight.postman_collection.json', developersApi.postmanCollectionText)}
          >
            {t('postman.collection')}
          </Button>
        </Box>
        {(info?.environments ?? []).map((environment) => (
          <Box key={environment.key} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mt: 1.25 }}>
            <Button
              variant="outlined"
              size="small"
              disabled={busy === environment.key}
              startIcon={<DownloadIcon />}
              onClick={() => download(environment.key, `insight-${environment.key}.postman_environment.json`, () => developersApi.postmanEnvironmentText(environment.key))}
            >
              {environmentName(translate, environment)}
            </Button>
            <Typography sx={{ fontFamily: MONO, fontSize: '0.8125rem', color: theme.custom.text.tertiary, overflowWrap: 'anywhere' }}>{environment.apiBaseUrl}</Typography>
          </Box>
        ))}
        <Box component="ol" sx={{ m: 0, mt: 2, pl: 2.5, color: theme.palette.text.secondary, fontSize: '0.875rem', lineHeight: 1.65 }}>
          {[0, 1, 2].map((i) => (
            <li key={i}>
              <InlineText text={t(`postman.steps.${i}`)} />
            </li>
          ))}
        </Box>
      </Card>

      <Card sx={cardSx}>
        <Typography component="h2" sx={h2}>
          {t('openapi.title')}
        </Typography>
        <Typography sx={body}>{t('openapi.text', { version: info?.version ?? '' })}</Typography>
        <Button variant="contained" size="small" disabled={!specText} startIcon={<DownloadIcon />} sx={{ mt: 1.5 }} onClick={() => saveAs('insight-openapi.json', specText)}>
          {t('openapi.button')}
        </Button>
        <Typography sx={{ ...body, mt: 1.5 }}>{t('openapi.note')}</Typography>
      </Card>
    </Box>
  );
};
